import { getApiV1Url } from '../config/api';
import { getUserInfo } from './auth';
import { CommonUtils } from './commonUtils';

export interface PinSendListRequest {
  projectCode: string;
  appCode: string;
  receiver: string;
}

export interface PinSendItem {
  pin: string;
  sentAt: string;
}

interface PinSendListApiResponse {
  errors?: {
    error?: Array<{
      code?: number;
      target?: string;
      message?: string;
    }>;
  };
  data?: {
    customerPinLogs?: Array<{
      pinCode?: string;
      createdAt?: string;
      updatedAt?: string;
    }>;
    pinLogs?: Array<{
      pinCode?: string;
      createdAt?: string;
      updatedAt?: string;
    }>;
    pinSends?: Array<{
      pin?: string;
      pinCode?: string;
      sentAt?: string;
      sendAt?: string;
      createdAt?: string;
    }>;
    items?: Array<{
      pin?: string;
      pinCode?: string;
      sentAt?: string;
      sendAt?: string;
      createdAt?: string;
    }>;
  };
}

interface RawPinSendItem {
  pin?: string;
  pinCode?: string;
  sentAt?: string;
  sendAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const getPinSendList = async (
  params: PinSendListRequest
): Promise<PinSendItem[]> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  try {
    const endpoint = `/projects/${encodeURIComponent(params.projectCode)}/apps/${encodeURIComponent(params.appCode)}/customer-pin-logs/destination/${encodeURIComponent(params.receiver)}`;
    const response = await fetch(
      getApiV1Url(endpoint),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
        },
      }
    );

    const result: PinSendListApiResponse = await response.json().catch(() => ({}));

    if (result.errors?.error && result.errors.error.length > 0) {
      const errorMessage = result.errors.error
        .map((error) => error.message)
        .filter(Boolean)
        .join(', ');
      throw new Error(errorMessage || '핀 발송 목록 조회에 실패했습니다.');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const rawItems: RawPinSendItem[] =
      (result.data?.customerPinLogs as RawPinSendItem[] | undefined) ??
      (result.data?.pinLogs as RawPinSendItem[] | undefined) ??
      (result.data?.pinSends as RawPinSendItem[] | undefined) ??
      (result.data?.items as RawPinSendItem[] | undefined) ??
      [];

    return rawItems.map((item) => ({
      pin: item.pinCode ?? item.pin ?? '-',
      sentAt: item.sentAt ?? item.sendAt ?? item.createdAt ?? item.updatedAt ?? '',
    }));
  } catch (error: any) {
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
